/**
 * Uploads the current Word document to signus-service and creates a standard
 * draft ready for signing.
 *
 * Office.js `getFileAsync` returns the document as a sequence of compressed
 * slices. We reassemble them into a single Uint8Array and POST it as a
 * multipart file upload to `POST /v0/word/teams/:teamSlug/draft`.
 */

/* global Office */

import { SIGNUS_API_URL } from './constants';
import { extractErrorMessage, fetchWithTokenRefresh } from './apiFetch';

/** Reassemble all Office.js file slices into one Uint8Array. */
function collectSlices(file: Office.File): Promise<Uint8Array> {
  return new Promise((resolve, reject) => {
    const sliceCount = file.sliceCount;
    const slices: Uint8Array[] = new Array(sliceCount);
    let remaining = sliceCount;

    for (let i = 0; i < sliceCount; i++) {
      file.getSliceAsync(i, result => {
        if (result.status === Office.AsyncResultStatus.Failed) {
          file.closeAsync(() => {});
          reject(new Error(`Failed to read document slice ${i}: ${result.error.message}`));
          return;
        }
        slices[result.value.index] = new Uint8Array(result.value.data as ArrayBuffer);
        remaining--;
        if (remaining === 0) {
          const totalLen = slices.reduce((sum, s) => sum + s.length, 0);
          const out = new Uint8Array(totalLen);
          let offset = 0;
          for (const s of slices) {
            out.set(s, offset);
            offset += s.length;
          }
          resolve(out);
        }
      });
    }
  });
}

/** Fallback name used when Word has no filename for the document yet (never saved). */
const DEFAULT_DOCUMENT_NAME = 'Untitled document.docx';

/** Pull the basename off a document url/path, as returned by `getFilePropertiesAsync`. */
function basenameFromUrl(url: string): string | null {
  if (!url) {
    return null;
  }
  const decoded = decodeURIComponent(url);
  const lastSegment = decoded.split(/[\\/]/).pop();
  return lastSegment || null;
}

/** Get the filename of the document currently open in Word, e.g. "Contract.docx". */
export function getDocumentFilename(): Promise<string> {
  return new Promise(resolve => {
    Office.context.document.getFilePropertiesAsync(result => {
      if (result.status === Office.AsyncResultStatus.Failed) {
        resolve(DEFAULT_DOCUMENT_NAME);
        return;
      }
      resolve(basenameFromUrl(result.value.url) ?? DEFAULT_DOCUMENT_NAME);
    });
  });
}

/** Export the current document as a .docx Blob via Office.js. */
export function getDocumentBlob(): Promise<Blob> {
  return new Promise((resolve, reject) => {
    Office.context.document.getFileAsync(
      Office.FileType.Compressed,
      { sliceSize: 65536 },
      async result => {
        if (result.status === Office.AsyncResultStatus.Failed) {
          reject(new Error(`Could not export document: ${result.error.message}`));
          return;
        }
        const file = result.value;
        try {
          const bytes = await collectSlices(file);
          file.closeAsync(() => {});
          resolve(
            new Blob([bytes.buffer as ArrayBuffer], {
              type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            }),
          );
        } catch (e) {
          file.closeAsync(() => {});
          reject(e);
        }
      },
    );
  });
}

export interface CreateDraftResult {
  documentId: string;
}

/**
 * Upload `blob` to signus-service and create a standard draft named `filename`.
 * Returns the `documentId` from the response.
 */
export async function createDraftFromDocument(
  blob: Blob,
  filename: string,
  teamSlug: string,
  accessToken: string,
): Promise<CreateDraftResult> {
  const res = await fetchWithTokenRefresh(
    `${SIGNUS_API_URL}/v0/word/teams/${encodeURIComponent(teamSlug)}/draft`,
    accessToken,
    token => {
      const form = new FormData();
      form.append('file', blob, filename);
      return { method: 'POST', headers: { Authorization: token }, body: form };
    },
  );

  const json = await res.json();

  if (!res.ok) {
    throw new Error(extractErrorMessage(json, `Upload failed (${res.status})`));
  }

  const payload = (json.payload ?? json) as Record<string, unknown>;
  return { documentId: payload.documentId as string };
}
